import { gql } from '@apollo/client';

const CLIENT_FIELDS = gql`
  fragment ClientFields on User {
    id
    uuid
    nickname
    phone
    deposit
    bonus
    last_client_activity
    dob
    first_name
    last_name
    middle_name
    pausable_info {
      id
      duration
      elapsed
      expires_at
      tariff {
        title
      }
    }
    group {
      uuid
      title
      discount
    }
    roles {
      id
      alias
      title
    }
    last_comment {
      text
      is_important
    }
    total_hours
    created_at
    banned_at
    disabled_at
    user_discount
    discounts {
      type
      value
    }
    avatar_url
    unverified
  }
`;

const CLIENT_FIELDS_WITHOUT_TARIFFS = gql`
  fragment ClientFieldsWithoutTariffs on User {
    id
    uuid
    nickname
    phone
    deposit
    bonus
    last_client_activity
    dob
    first_name
    last_name
    middle_name
    group {
      uuid
      title
      discount
    }
    roles {
      id
      alias
      title
    }
    last_comment {
      text
      is_important
    }
    total_hours
    created_at
    banned_at
    disabled_at
    user_discount
    discounts {
      type
      value
    }
    avatar_url
    unverified
  }
`;

export const SEARCH_CLIENTS = gql`
  ${CLIENT_FIELDS}
  query clients($input: ClientsInput, $first: Int, $page: Int) {
    clients(input: $input, first: $first, page: $page) {
      paginatorInfo {
        count
        currentPage
        lastPage
      }
      data {
        ...ClientFields
      }
    }
  }
`;

export const SEARCH_CLIENTS_LIGHT = gql`
  query clients($input: ClientsInput, $first: Int, $page: Int) {
    clients(input: $input, first: $first, page: $page) {
      paginatorInfo {
        count
        currentPage
        lastPage
      }
      data {
        uuid
        phone
        nickname
      }
    }
  }
`;

export const SEARCH_CLIENTS_LIGHT_WORKER = gql`
  query clients($input: ClientsInput, $first: Int, $page: Int) {
    clients(input: $input, first: $first, page: $page) {
      paginatorInfo {
        count
        currentPage
        lastPage
      }
      data {
        uuid
        phone
        nickname
        roles {
          id
          alias
          title
        }
      }
    }
  }
`;

export const SEARCH_CLIENTS_FOR_ACTIVE_OPERATOR = gql`
  ${CLIENT_FIELDS}
  query searchClients($input: ClientsInput, $first: Int, $page: Int) {
    searchClients(input: $input, first: $first, page: $page) {
      paginatorInfo {
        count
        currentPage
        lastPage
      }
      data {
        ...ClientFields
      }
    }
  }
`;

export const GET_USER_BY_UUID = gql`
  ${CLIENT_FIELDS}
  query user($uuid: String!) {
    user(uuid: $uuid) {
      ...ClientFields
    }
  }
`;

export const GET_USER_BY_UUID_WITHOUT_TARIFFS = gql`
  ${CLIENT_FIELDS_WITHOUT_TARIFFS}
  query user($uuid: String!) {
    user(uuid: $uuid) {
      ...ClientFieldsWithoutTariffs
    }
  }
`;

export const GET_USER_ID = gql`
  query user($uuid: String!) {
    user(uuid: $uuid) {
      uuid
      id
    }
  }
`;

export const GET_PAUSABLE_TARIFF = gql`
  query pausableClientSessions($clientId: String) {
    pausableClientSessions(client_id: $clientId) {
      duration
      elapsed
      expires_at
      id
      status
      host_group {
        title
      }
      tariff {
        title
        schedules {
          end
          start
        }
      }
    }
  }
`;

export const BAN_CLIENT = gql`
  mutation banClient($uuid: String!, $input: FlagInput!) {
    banClient(uuid: $uuid, input: $input) {
      uuid
      banned_at
    }
  }
`;

export const SET_USER_DEPOSIT = gql`
  mutation setDeposit($input: SetDepositInput!) {
    setDeposit(input: $input) {
      uuid
      deposit
    }
  }
`;

export const SET_USER_DISCOUNT = gql`
  mutation setUserDiscount($input: SetUserDiscountInput!) {
    setUserDiscount(input: $input) {
      uuid
      user_discount
      discounts {
        type
        value
      }
    }
  }
`;

export const GET_CLIENT_PAYMENTS = gql`
  query getPaymentsByClientId($uuid: String!, $page: Int, $first: Int) {
    getPaymentsByClientId(uuid: $uuid, page: $page, first: $first) {
      paginatorInfo {
        currentPage
        lastPage
      }
      data {
        created_at
        title
        amount
        sum
        card_sum
        cash_sum
        bonus
        worker {
          last_name
          first_name
        }
        paymentMethod
        is_refunded
        items {
          id
          amount
          type
          sum
          entity {
            ... on Service {
              id
              title
              cost
              vat
              tax_system
            }
            ... on Good {
              id
              title
              cost
              amount
              vat
              tax_system
            }
            ... on Tariff {
              id
              title
            }
          }
        }
      }
    }
  }
`;

export const GET_CLIENT_DEPOSIT_TRANSFERS = gql`
  query GetClientDepositTransfers($uuid: String!, $page: Int, $first: Int) {
    getClientDepositTransfers(uuid: $uuid, page: $page, first: $first) {
      data {
        club {
          id
          name
          address
        }
        type
        sum
        created_at
      }
      paginatorInfo {
        lastPage
        currentPage
      }
    }
  }
`;
export const CLIENTS = gql`
  query clients($input: ClientsInput, $first: Int, $page: Int) {
    clients(input: $input, first: $first, page: $page) {
      total_deposits
      paginatorInfo {
        count
        currentPage
        lastPage
        total
      }
      data {
        id
        uuid
        nickname
        phone
        last_client_activity
        user_discount
        discounts {
          type
          value
        }
        group {
          uuid
          title
          discount
        }
        deposit
        bonus
        banned_at
        created_at
        unverified
        avatar_url
        roles {
          alias
        }
      }
    }
  }
`;

export const EXPORT_CLIENTS = gql`
  query exportClients {
    exportUsers
  }
`;

export const TOTAL_CLIENTS = gql`
  query totalClients($input: ClientsInput, $first: Int, $page: Int) {
    clients(input: $input, first: $first, page: $page) {
      total_deposits
      paginatorInfo {
        total
      }
    }
  }
`;

export const GROUPS_TITLES = gql`
  query getGroupTitles {
    userGroups {
      uuid
      title
      discount
    }
  }
`;

export const USER_GROUP_FRAGMENT = gql`
  fragment UserGroupFragment on UserGroup {
    uuid
    title
    user_count
    discount
    created_at
  }
`;

export const USER_GROUPS = gql`
  ${USER_GROUP_FRAGMENT}
  query userGroups {
    userGroups {
      ...UserGroupFragment
    }
  }
`;

export const CHANGE_USERS_GROUP = gql`
  ${USER_GROUP_FRAGMENT}
  mutation changeUsersGroup($input: ChangeUsersGroupInput!) {
    changeUsersGroup(input: $input) {
      ...UserGroupFragment
    }
  }
`;
export const CREATE_USER_GROUP = gql`
  ${USER_GROUP_FRAGMENT}
  mutation createUserGroup($input: UserGroupInput!) {
    createUserGroup(input: $input) {
      ...UserGroupFragment
    }
  }
`;

export const UPDATE_USER_GROUP = gql`
  ${USER_GROUP_FRAGMENT}
  mutation updateUserGroup($uuid: String!, $input: UserGroupInput!) {
    updateUserGroup(uuid: $uuid, input: $input) {
      ...UserGroupFragment
    }
  }
`;

export const DELETE_USER_GROUP = gql`
  ${USER_GROUP_FRAGMENT}
  mutation deleteUserGroup($uuid: String!) {
    deleteUserGroup(uuid: $uuid) {
      ...UserGroupFragment
    }
  }
`;
